import styled from 'styled-components';

import { colors, radius } from '../../styles/tokens';

export const Container = styled.div`
  width: 100%;
  min-width: 640px;
  max-width: 720px;

  display: flex;
  flex-direction: column;

  background-color: ${colors.surfaceContainerLow};

  @media (max-width: 1199px) {
    min-width: auto;
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding: 24px 28px 28px;
  max-height: 70vh;
  overflow-y: auto;
`;

export const StepTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const ModeCard = styled.button<{ selected?: boolean }>`
  text-align: left;
  padding: 18px;
  border-radius: ${radius.md};
  border: 1px solid
    ${props => (props.selected ? colors.primary : colors.outlineVariant)}${props => (props.selected ? '' : '40')};
  background-color: ${props =>
    props.selected ? `${colors.primary}1a` : colors.surfaceContainer};
  cursor: pointer;
  transition: border-color 0.15s;

  strong {
    display: block;
    font-size: 14.5px;
    color: ${colors.onSurface};
    margin-bottom: 6px;
  }

  span {
    font-size: 12.5px;
    line-height: 1.5;
    color: ${colors.onSurfaceVariant};
  }

  &:hover {
    border-color: ${colors.primary};
  }
`;

export const FieldRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 140px;
  }
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 6px;
`;

export const NativeInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.outlineVariant}40;
  background-color: ${colors.surfaceContainer};
  color: ${colors.onSurface};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  /* Remove as setas do input numérico (Chrome/Safari/Edge e Firefox). */
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const NativeSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.outlineVariant}40;
  background-color: ${colors.surfaceContainer};
  color: ${colors.onSurface};
  font-size: 14px;
`;

export const GroupsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const GroupRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 8px 10px;
  border-radius: ${radius.sm};
  background-color: ${colors.surfaceContainer};

  font-size: 13.5px;
  color: ${colors.onSurface};

  input[type='checkbox'] {
    accent-color: ${colors.primary};
  }

  span {
    flex: 1;
  }
`;

export const QuantityField = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;

  font-size: 10.5px;
  color: ${colors.onSurfaceVariant};
`;

export const QuantityInput = styled.input`
  width: 40px;
  box-sizing: border-box;
  padding: 5px 4px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.outlineVariant}40;
  background-color: ${colors.surfaceContainerLow};
  color: ${colors.onSurface};
  font-size: 12.5px;
  text-align: center;

  /* Remove as setas do input numérico (Chrome/Safari/Edge e Firefox). */
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const PreviewDay = styled.div`
  padding: 14px 16px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainer};
  border: 1px solid ${colors.outlineVariant}30;

  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PreviewDayHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;

  strong {
    font-size: 13.5px;
    color: ${colors.onSurface};
  }

  span {
    font-size: 12px;
    color: ${colors.onSurfaceVariant};
  }
`;

export const ExistingLabel = styled.p`
  margin: 0 0 4px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.onSurfaceVariant};
`;

export const ExerciseRow = styled.div<{ dimmed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 8px 10px;
  border-radius: ${radius.sm};
  background-color: ${colors.surfaceContainerLow};
  opacity: ${props => (props.dimmed ? 0.6 : 1)};

  font-size: 13px;
  color: ${colors.onSurface};

  span.muscle {
    color: ${colors.onSurfaceVariant};
    font-size: 11.5px;
  }
`;

export const RemoveButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  color: ${colors.onSurfaceVariant};
  display: flex;
  align-items: center;
  padding: 2px;

  &:hover {
    color: ${colors.error};
  }
`;

export const WarningText = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${colors.tertiary};
`;

export const DestructiveWarning = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-radius: ${radius.sm};
  background-color: ${colors.error}1a;
  color: ${colors.error};
  font-size: 12.5px;
  line-height: 1.5;
`;

export const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 18px 28px;
  border-top: 1px solid ${colors.outlineVariant}30;
`;

export const ActionsRight = styled.div`
  display: flex;
  gap: 10px;
`;

export const GhostButton = styled.button`
  padding: 10px 16px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.outlineVariant}40;
  background: transparent;
  color: ${colors.onSurface};
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled.button`
  padding: 10px 18px;
  border-radius: ${radius.sm};
  border: 0;
  background: ${colors.primary};
  color: ${colors.onPrimary};
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const AddExerciseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  padding: 9px 0;
  border-radius: ${radius.sm};
  border: 1px dashed ${colors.primary};
  background: transparent;
  color: ${colors.primary};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${colors.primary}0d;
  }
`;

export const EmptyPreview = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${colors.onSurfaceVariant};
`;
